import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { BaseService, QueryOptions, PaginatedResponse } from './base.service';
import {
  Organization,
  OrganizationSettings,
  PayrollApprover,
  PayrollApproverCandidate,
} from '../model/organization.model';

export interface ResetResult {
  success: boolean;
  deleted: Record<string, number>;
  storage_paths?: string[];
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends BaseService<Organization> {
  protected override tableName = 'organizations';

  /**
   * Emits when an organization is updated - use to trigger menu/UI refresh
   */
  private organizationUpdatedSubject = new Subject<string>();
  organizationUpdated$ = this.organizationUpdatedSubject.asObservable();

  private readonly settingsFields: (keyof OrganizationSettings)[] = [
    'business_type',
    // Product surface chosen at signup (plan 69). No form control writes it, but
    // flatten() lifts it to the top level, so it must be listed here or a write
    // built from a flattened org would send it as a non-existent column.
    'mode',
    'logo_url',
    'business_image_url',
    'require_customer_name',
    'show_cover_count',
    'include_company_image',
    'allow_tips',
    'allow_special_instructions',
    'payment_gateway_enabled',
    'payment_mode',
    'allow_split_bill',
    'allow_table_transfer',
    'hide_sold_out_products',
    'primary_color',
    'success_color',
    'warning_color',
    'danger_color',
    'info_color',
    'disable_order_landing_page',
    'enabled_payment_methods',
    'table_auto_release_minutes',
    'payment_collection_mode',
    'tab_payment_timing',
    'tax_enabled',
    'tax_rate',
    'tax_inclusive_pricing',
    'tax_tin',
    'tax_business_name',
    'tax_business_address',
    'receipt_prefix',
    'official_receipts_enabled',
    'auto_z_reading',
    'public_theme',
    'enable_guest_ordering',
    'enable_table_layout',
    'takeaway_payment_timing',
    'receipt_print_mode',
    'show_kds',
    'enable_ai_assistant',
    'require_payment_reference',
    'qr_code_show_logo',
    'qr_code_color',
  ];

  /**
   * Helper to flatten settings from JSONB column into the main object
   */
  private flatten(org: Organization): Organization {
    if (!org) return org;
    const { settings, ...rest } = org;
    return {
      ...rest,
      settings,
      ...(settings || {}),
    } as Organization;
  }

  /**
   * Helper to nest settings into the JSONB column for writes
   */
  private nest(data: Partial<Organization>): Partial<Organization> {
    const payload = { ...data };
    const settings: OrganizationSettings = { ...(data.settings || {}) };
    const p = payload as Record<string, unknown>;

    this.settingsFields.forEach((field) => {
      if (field in p) {
        (settings as Record<string, unknown>)[field] = p[field];
        delete p[field];
      }
    });

    if (Object.keys(settings).length > 0) {
      p['settings'] = settings;
    }

    return p as Partial<Organization>;
  }

  override async getById(id: string): Promise<Organization | null> {
    const org = await super.getById(id);
    return org ? this.flatten(org) : null;
  }

  override async getAll(options: QueryOptions = {}): Promise<PaginatedResponse<Organization>> {
    const response = await super.getAll(options);
    return {
      ...response,
      data: response.data.map((org) => this.flatten(org)),
    };
  }

  override async create(
    data: Omit<Organization, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<Organization> {
    const nested = this.nest(data as Partial<Organization>);
    // The cast to any here is needed because the nested payload structure
    // (with packaged JSONB) doesn't perfectly match the flattened Omit type.
    const result = await super.create(nested as unknown as Omit<Organization, 'id' | 'created_at' | 'updated_at'>);
    return this.flatten(result);
  }

  override async update(
    id: string,
    data: Partial<Omit<Organization, 'id' | 'created_at' | 'updated_at'>>,
  ): Promise<Organization> {
    const nested = this.nest(data as Partial<Organization>);
    const result = await super.update(id, nested as unknown as Partial<Organization>);
    // Notify subscribers that org was updated (for menu refresh, etc.)
    this.organizationUpdatedSubject.next(id);
    return this.flatten(result);
  }

  /**
   * Updates organization settings specifically.
   */
  async updateSettings(id: string, settings: Partial<OrganizationSettings>): Promise<void> {
    await this.update(id, settings as unknown as Partial<Omit<Organization, 'id' | 'created_at' | 'updated_at'>>);
  }

  // ==========================================================================
  // Payroll approver (organizations.payroll_approver_id)
  // ==========================================================================

  /**
   * Roles that can plausibly sign off a payroll sheet. Cashier and Kitchen are
   * excluded — the setting names a signatory, not a staff directory.
   */
  private static readonly APPROVER_ROLE_NAMES = ['Owner', 'Admin', 'Manager'];

  /**
   * Candidates for the "Approved by" line: active members of the organization
   * holding an approving role, current user included (an Owner naming
   * themselves is the common case, so this deliberately does NOT reuse
   * UserService, which excludes the caller).
   *
   * Reads `v_user_management` (security_invoker), so RLS scopes it. The
   * explicit organization_id equality is the super-admin filter required of
   * every custom query — mirrors `BaseService.getAll()`.
   */
  async getPayrollApproverCandidates(): Promise<PayrollApproverCandidate[]> {
    try {
      const organizationId = this.orgContext.effectiveOrganizationId();
      if (!organizationId) return [];

      const { data, error } = await this.supabase.client
        .from('v_user_management')
        .select('user_id, full_name, user_email, role_name')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .in('role_name', OrganizationService.APPROVER_ROLE_NAMES)
        .order('full_name', { ascending: true });

      if (error) throw error;

      // One row per user_roles row; collapse in case a user somehow holds two.
      const seen = new Set<string>();
      const candidates: PayrollApproverCandidate[] = [];
      for (const row of (data || []) as PayrollApproverCandidate[]) {
        if (!row.user_id || seen.has(row.user_id)) continue;
        seen.add(row.user_id);
        candidates.push({
          user_id: row.user_id,
          full_name: row.full_name || row.user_email,
          user_email: row.user_email,
          role_name: row.role_name ?? null,
        });
      }
      return candidates;
    } catch (error) {
      throw this.errorHandler.handleError(error, 'OrganizationService.getPayrollApproverCandidates');
    }
  }

  /**
   * The approver's display name, for the payroll sheet's "Approved by" line.
   * Returns null when no approver is set, or when the one that is set is no
   * longer an active member.
   *
   * Goes through the `get_payroll_approver` RPC rather than reading `profiles`
   * directly: profiles RLS requires `member:view`, which a payroll clerk
   * holding only `attendance:view` does not have.
   */
  async getPayrollApproverName(organizationId?: string): Promise<string | null> {
    const approver = await this.getPayrollApprover(organizationId);
    return approver?.name ?? null;
  }

  /** Full approver record (id, name, email). See {@link getPayrollApproverName}. */
  async getPayrollApprover(organizationId?: string): Promise<PayrollApprover | null> {
    try {
      const orgId = organizationId ?? this.orgContext.effectiveOrganizationId();
      if (!orgId) return null;

      const { data, error } = await this.supabase.client.rpc('get_payroll_approver', {
        p_organization_id: orgId,
      });

      if (error) throw error;

      const result = data as PayrollApprover | null;
      if (!result || result.error) return null;
      return result;
    } catch (error) {
      // A missing signatory must never block the payslip from printing.
      this.logger.warn('OrganizationService: could not resolve payroll approver', error);
      return null;
    }
  }

  async resetOrganizationData(
    organizationId: string,
    categories: string[],
  ): Promise<ResetResult> {
    const { data, error } = await this.supabase.client.rpc('reset_organization_data', {
      p_organization_id: organizationId,
      p_categories: categories,
    });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);

    // Clean up storage files
    if (data.storage_paths?.length) {
      await this.supabase.client.storage.from('pos-media').remove(data.storage_paths);
    }

    // Clear all caches
    window.dispatchEvent(new Event('auth:clear-caches'));
    return data;
  }
}
